import { useTranslation } from 'react-i18next';
import {
  Headings,
  ModalOverlay,
  SentaraLogo,
  MDLiveLogo,
  VirtualIcon,
  Loader,
} from '@sentara-health/optimahealth_web';
import { VirtualVisitModalBox } from 'templates';
import { errorhandler, navigateToExternalUrl, SuperScript } from 'common';
import { useState, useContext } from 'react';
import { useMySSOUrl } from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext } from 'context';

interface UrgentCarePopUpProps {
  closeModal: () => void;
  mdliveCopay?: string;
  sentaraCopay?: string;
}
function UrgentCarePopUp({
  closeModal,
  mdliveCopay,
  sentaraCopay,
}: Readonly<UrgentCarePopUpProps>) {
  // Initialize translation hook
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  // Initialize navigation hook
  const { SSOUrlType } = useMySSOUrl();
  const { ssoEndPoints, getFeature } = useContext(GlobalContext);
  const isMedicaidOrDsnpMember =
    !!getFeature?.HomeAddressMedicaid || !!getFeature?.HomeAddressDSNP;
  const sentaraDisclaimerList = [
    {
      superScript: <sup>{SuperScript.VIRTUAL_DISCLAIMER_ONE}</sup>,
      text: t('virtualVisitCare.disclaimerNote2Common'),
      style: 'font-size-14 mt-3',
    }!,
    {
      superScript: <sup>{SuperScript.VIRTUAL_DISCLAIMER_TWO}</sup>,
      text: t('virtualVisitCare.disclaimerNote1Common'),
      style: 'font-size-14 mt-2',
    }!,
    {
      text: t('virtualVisitCare.NurseOnCall.sentaraContent.disclaimerNote2'),
      style: 'font-size-14 mt-2',
    }!,
  ];

  const mdLiveDisclaimerList = [
    {
      superScript: <sup>{SuperScript.VIRTUAL_DISCLAIMER_TWO}</sup>,
      text: t('virtualVisitCare.disclaimerNote1Common'),
      style: 'font-size-14 mt-2',
    }!,
    {
      text: t('virtualVisitCare.NurseOnCall.sentaraContent.disclaimerNote2'),
      style: 'font-size-14 mt-2',
    }!,
  ];

  const getMDliveSSOUrlFromAPI = async (type: string) => {
    setLoading(true);
    const res = await SSOUrlType(type); // Get SSO URL from API
    if (res?.data?.ssoUrl) {
      navigateToExternalUrl(res?.data?.ssoUrl); // Open SSO URL in a new tab
    } else if (res?.errorCode) {
      errorhandler(res?.errorCode);
    }
    setLoading(false);
  };

  return (
    <>
      <ModalOverlay
        className="virtual-visit-selection"
        data-testid={'primaryCareSelectionId'}
        isOpen
        onClose={closeModal}
        overlayInfo="primary care selection"
      >
        <div>
          <Headings
            level={2}
            text={t('virtualVisitCare.selectProvider')}
            variant="regular"
          />
          <div className="d-flex flex-column flex-lg-row gap-4 mt-2 justify-content-center">
            <div className="curved-box">
              <VirtualVisitModalBox
                logoImage={<SentaraLogo />}
                title={t('virtualVisitCare.sentaraHealthTitle')}
                costValue={sentaraCopay}
                superTextOne={SuperScript.VIRTUAL_DISCLAIMER_ONE}
                superTextTwo={SuperScript.VIRTUAL_DISCLAIMER_TWO}
                costInfo={t('virtualVisitCare.costInfo')}
                badgeImage={<VirtualIcon />}
                content={t(
                  'virtualVisitCare.UrgentCare.sentaraContent.serviceContent'
                )}
                buttonLabel={t('virtualVisitCare.scheduleAppointment')}
                buttonTestId="scheduleSentaraAppointment"
                buttonOnClick={() => {
                  navigateToExternalUrl(ssoEndPoints?.PRIMARY_CARE_URL); // Navigate to the URL
                }}
                minHeight={'252'}
                disclaimers={sentaraDisclaimerList}
                deductibleBlock={!isMedicaidOrDsnpMember}
                deductibleText={t('virtualVisitCare.deductibleNote')}
              />
            </div>
            <div className=" curved-box">
              <VirtualVisitModalBox
                logoImage={<MDLiveLogo />}
                title={t('virtualVisitCare.mdliveTitle')}
                costInfo={t('virtualVisitCare.costInfo')}
                badgeImage={<VirtualIcon />}
                content={t('virtualVisitCare.serviceContent')}
                superTextTwo={SuperScript.VIRTUAL_DISCLAIMER_TWO}
                costValue={mdliveCopay}
                buttonLabel={t('virtualVisitCare.scheduleAppointment')}
                buttonTestId="scheduleMDliveAppointment"
                buttonOnClick={() => {
                  getMDliveSSOUrlFromAPI(ssoEndPoints?.VirtualVisitSSO);
                }}
                minHeight={'250'}
                disclaimers={mdLiveDisclaimerList}
                deductibleBlock={!isMedicaidOrDsnpMember}
                deductibleText={t('virtualVisitCare.deductibleNote')}
              />
            </div>
          </div>
        </div>
      </ModalOverlay>
      {loading ? <Loader /> : null}
    </>
  );
}

export default UrgentCarePopUp;
